import { useForm } from "react-hook-form";
import { RotatingLines } from "react-loader-spinner";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MainContainer from "../../../components/MainContainer";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { isObjectEmpty, xReqId } from "../../../utils";
import NotFound from "../../NotFound";

const ChosenMinorPage = () => {
	const { register, formState, handleSubmit, watch } = useForm({
		mode: "all",
		defaultValues: {
			ticketCode: "",
			afm: "",
		},
	});
	const navigate = useNavigate();
	const location = useLocation();
	const selectedMinor = location.state?.selectedMinor;

	useDocumentTitle("Στοιχεία συνοδείας ανηλίκου");

	if (!selectedMinor) {
		return <NotFound />;
	}

	const hasErrors = !isObjectEmpty(formState.errors);
	const valuesIsEmptyObject = isObjectEmpty(watch());

	const disabledBtn =
		hasErrors || valuesIsEmptyObject || watch("ticketCode")?.trim() === "" || watch("afm")?.trim() === "" || formState.isSubmitting;

	const submit = async () => {
		// Determine the API endpoint based on the user type.
		// const endpoint = user === "foreigner" ? "identify" : "personalize";

		// Build the base URL for the API endpoint.
		const url = "/api/tickets/dependents/declarations/indicate";

		// Construct the ticket data object from the form inputs.
		const companionData = {
			publicCode: watch("ticketCode").trim(),
			afm: watch("afm").trim(),
			amka: selectedMinor?.amka,
		};

		try {
			// Send a POST request to the API endpoint with the ticket data.
			const res = await fetch(url, {
				method: "POST",
				credentials: "include", // Include cookies for authentication.
				headers: { "Content-Type": "application/json", "x-request-id": xReqId }, // Specify the request body content type.
				body: JSON.stringify(companionData), // Serialize the ticket data to JSON.
			});

			if (res.ok) {
				const responseData = await res.json();
				const payload = { ...companionData, ...responseData };
				navigate("/tickets/success-companion", {
					state: { payload, type: "companion" },
				});
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			const genericErrorMessage =
				"Προέκυψε σφάλμα συστήματος. Παρακαλώ δοκιμάστε αργότερα και εάν το πρόβλημα εξακολουθεί επικοινωνήστε με την ομάδα υποστήριξης.";
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.exceptions?.errors?.[0]?.errorDescription;
				toast.error(appError ?? genericErrorMessage);
			} catch {
				toast.error(genericErrorMessage);
			}
		}
	};

	return (
		<MainContainer>
			<div className="col-lg-12">
				<Link to="/tickets/choose-minor" className="btn btn-back mb-4">
					<svg className="govgr-svg-icon--caret govgr-svg-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
						<path d="M18,22V2L6,12L18,22z"></path>
					</svg>
					Πίσω
				</Link>
				<p className="mb-2">Ονοματεπώνυμο ανηλίκου</p>
				<h1 className="mb-4">
					{selectedMinor?.firstName} {selectedMinor?.lastName} (γεν. {selectedMinor?.birthdate})
				</h1>
				<div className="row">
					<div className="col-md-6">
						<form noValidate onSubmit={handleSubmit(submit)}>
							<fieldset className="border-0 p-0 m-0 mb-5">
								<legend className="h2 mb-4">Στοιχεία εισιτήριου</legend>
								<div>
									<label htmlFor="ticketCode" className="form-label">
										Κωδικός εισιτηρίου <span className="required">*</span>
									</label>
									<input
										required
										id="ticketCode"
										onInput={(e) => {
											e.target.value = e.target.value.replace(/\D/g, "");
										}}
										aria-describedby="ticketCode-help"
										{...register("ticketCode", {
											maxLength: {
												value: 12,
											},
											minLength: {
												value: 12,
											},
											pattern: {
												value: /^\d{12}$/,
											},
										})}
										className={`form-control form-control-lg ${formState.errors.ticketCode ? "is-invalid" : ""}`}
									/>
									<div id="ticketCode-help" className={`form-text ${formState.errors.ticketCode ? "error text-danger" : ""}`}>
										12ψήφιος κωδικός
									</div>
								</div>
							</fieldset>
							<fieldset className="border-0 p-0 m-0">
								<legend className="h2 mb-4">Στοιχεία συνοδού</legend>
								<div className="mb-4">
									<label htmlFor="companionAFM" className="form-label">
										ΑΦΜ συνοδού <span className="required">*</span>
									</label>
									<input
										required
										id="companionAFM"
										onInput={(e) => {
											e.target.value = e.target.value.replace(/\D/g, "");
										}}
										aria-describedby="companionAFM-help"
										{...register("afm", {
											maxLength: {
												value: 9,
											},
											minLength: {
												value: 9,
											},
											pattern: {
												value: /^\d{9}$/,
											},
										})}
										className={`form-control form-control-lg ${formState.errors.afm ? "is-invalid" : ""}`}
									/>
									<div id="companionAFM-help" className={`form-text ${formState.errors.afm ? "error text-danger" : ""}`}>
										9ψήφιος κωδικός
									</div>
								</div>
							</fieldset>
							{/* <div className="p-4 border bg-body-tertiary border-success rounded mb-4">
                <p className="mb-0">
                  Το εισιτήριο αντιστοιχεί στον χρήστη{" "}
                  <strong>Ja*** *****</strong>.
                </p>
              </div> */}
							<button type="submit" disabled={disabledBtn} className="btn btn-primary btn-lg btn-login">
								{formState.isSubmitting ? (
									<div className="d-flex justify-content-center align-items-center">
										<RotatingLines
											visible={true}
											color="grey"
											strokeWidth="5"
											strokeColor="white"
											animationDuration="0.75"
											ariaLabel="rotating-lines-loading"
											wrapperStyle={{}}
											wrapperClass=""
										/>
										<div> Αποστολή αιτήματος αποδοχής</div>
									</div>
								) : (
									" Αποστολή αιτήματος αποδοχής"
								)}
							</button>
						</form>
					</div>
				</div>
			</div>
		</MainContainer>
	);
};
export default ChosenMinorPage;
